import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Broom, X, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { CONFIRM_WORD } from "./cleanStartLabels";
import { CompanyList, PageList, PeopleList } from "./CleanStartLists";
import { CountsTable, KeptNote } from "./CleanStartSummary";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

function useCleanStartPreview(open, keepPages, onClose) {
  const [preview, setPreview] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) { setPreview(null); return; }
    api.post("/admin/clean-start/preview", { keep_page_ids: keepPages })
      .then((r) => setPreview(r.data))
      .catch((e) => { toast.error(errText(e, "Could not load the clean start preview")); closeRef.current?.(); });
  }, [open, keepPages]);
  return preview;
}

function Header({ onClose }) {
  return (
    <div className="p-4 border-b-4 border-black flex items-start justify-between gap-3 bg-amber-100">
      <div className="flex items-start gap-3">
        <Broom size={28} weight="fill" className="text-amber-900 mt-0.5" />
        <div>
          <div className="label-mono">GO-LIVE</div>
          <h2 className="font-chivo font-black text-lg md:text-xl uppercase tracking-tighter">Clean start</h2>
        </div>
      </div>
      <button onClick={onClose} data-testid="clean-start-close"
        className="border border-black px-2 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white">
        <X size={10} weight="bold" /> Cancel
      </button>
    </div>
  );
}

function Intro() {
  return (
    <div className="flex items-start gap-2 text-xs text-amber-950 bg-amber-50 border border-amber-800 p-3" data-testid="clean-start-intro">
      <Warning size={16} weight="fill" className="shrink-0" />
      <div>
        Removes every buyer, media buyer, vendor and affiliate, all leads, and past activity (audit log, page visit stats,
        thank-you results). <b>Keeps</b> your owner and staff logins, the test accounts and what they use, and your company
        landing pages. A full undo copy is kept for 24 hours, and the demo accounts will not come back after a restart.
      </div>
    </div>
  );
}

export default function CleanStartDialog({ open, onClose, onDone }) {
  const [keepPages, setKeepPages] = useState([]);
  const [typed, setTyped] = useState("");
  const [running, setRunning] = useState(false);
  const preview = useCleanStartPreview(open, keepPages, onClose);

  useEffect(() => {
    if (open) { setTyped(""); setKeepPages([]); }
  }, [open]);

  if (!open) return null;
  const toggle = (id) => setKeepPages((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const total = Object.values(preview?.delete_counts || {}).reduce((a, b) => a + b, 0);
  const canRun = typed.trim() === CONFIRM_WORD && total > 0 && !running;

  const doRun = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/clean-start", { confirm: typed, keep_page_ids: keepPages });
      const n = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Clean start done: ${n.toLocaleString()} records removed. Undo is available for 24 hours.`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(errText(e, "Clean start failed. Nothing was removed if no undo copy was made."));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 md:p-6" role="dialog" aria-modal="true" data-testid="clean-start-modal">
      <div className="brut-card bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <Header onClose={onClose} />
        <div className="p-4 space-y-3">
          <Intro />
          {!preview ? <div className="label-mono text-zinc-500" data-testid="clean-start-loading">Checking what will be removed…</div> : (
            <>
              <div className="label-mono" data-testid="clean-start-total">Will remove {total.toLocaleString()} records</div>
              <PeopleList users={preview.users} />
              <CompanyList orgs={preview.orgs} />
              <PageList pages={preview.pages} extraKeep={keepPages} onToggle={toggle} />
              <CountsTable counts={preview.delete_counts} />
              <KeptNote untouched={preview.untouched} />
            </>
          )}
          <div>
            <label className="label-mono" htmlFor="clean-start-confirm">Type <span className="font-mono">{CONFIRM_WORD}</span> to unlock the button</label>
            <input id="clean-start-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={CONFIRM_WORD}
              autoComplete="off" data-testid="clean-start-confirm-input" className="w-full border border-black p-2 mt-1 font-mono text-xs" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t-4 border-black p-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white" data-testid="clean-start-cancel">Cancel</button>
          <button onClick={doRun} disabled={!canRun} data-testid="clean-start-run"
            className="bg-amber-500 text-black px-4 py-2 text-xs font-bold uppercase tracking-widest border border-black inline-flex items-center gap-2 disabled:opacity-40">
            <Broom size={12} weight="bold" /> {running ? "Cleaning…" : "Start clean now"}
          </button>
        </div>
      </div>
    </div>
  );
}
